import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { ListDisplayType } from '@/constants';
import { VIEW_CONFIG_STATUS, ViewConfigStatus } from '@/constants/firestore';
import { SERVICE_MODE } from '@/constants/plan';
import {
  guestDisplaySlice,
  GuestDisplayState,
  GuestStatusType,
  ShareActiveStatus,
  PlanInfo,
  initialState,
} from '@/states/slices/guestDisplaySlice';
import { AppDispatch, RootState, useAppDispatch } from '@/states/store';
import { defaultUiLanguage } from '@/utils/ui';

import { PlanInfo as ApiPlanInfo } from '../features/api/planInfoApi';

/**
 * ゲスト画面情報に関する情報を保存 hooks
 *
 * @returns
 */
export const useGuestDisplay = () => {
  const dispatch: AppDispatch = useAppDispatch();

  const {
    setDisplayType,
    setFontSize,
    setDestlang,
    setUiLang,
    setGuestStatus,
    setIsCSVDownload,
    setShareActiveChangeStatus,
    setIsOpenShareActiveDialog,
    setISOpenShareInactiveDialog,
    resetToInitialState,
    setPlanInfoFromApi,
    setPlanInfo,
  } = guestDisplaySlice.actions;

  const {
    displayType,
    fontSize,
    destlang,
    uiLang,
    guestStatus,
    isCSVDownload,
    shareActiveStatus,
    isOpenShareActiveDialog,
    isOpenShareInactiveDialog,
    planInfo,
  } = useSelector<RootState, GuestDisplayState>((state) => state.guestDisplay);

  /**
   * 翻訳元テキストの表示/非表示を更新
   */
  const changeDisplayType = useCallback(
    (value: ListDisplayType) => {
      dispatch(setDisplayType(value));
    },
    [dispatch, setDisplayType],
  );

  /**
   * 翻訳関連テキストの文字サイズを更新
   */
  const changeFontSize = useCallback(
    (value: number) => {
      dispatch(setFontSize(value));
    },
    [dispatch, setFontSize],
  );

  /**
   * 翻訳先言語を更新
   */
  const changeDestlang = useCallback(
    (value: string) => {
      dispatch(setDestlang(value));
    },
    [dispatch, setDestlang],
  );

  /**
   * UI言語を更新
   */
  const changeUiLanguage = useCallback(
    (value: string) => {
      dispatch(setUiLang(value));
    },
    [dispatch, setUiLang],
  );

  /**
   * ゲスト画面表示状況を更新
   */
  const changeGuestStatus = useCallback(
    (value: GuestStatusType) => {
      dispatch(setGuestStatus(value));
    },
    [dispatch, setGuestStatus],
  );

  /**
   * CSVダウンロードが許可されているか否かの値を更新
   */
  const changeIsCSVDownload = useCallback(
    (value: boolean) => {
      dispatch(setIsCSVDownload(value));
    },
    [dispatch, setIsCSVDownload],
  );

  /**
   * 共有状況の変更状態を更新
   */
  const changeShareActiveStatus = useCallback(
    (value: ShareActiveStatus) => {
      dispatch(setShareActiveChangeStatus(value));
    },
    [dispatch, setShareActiveChangeStatus],
  );

  /**
   * 共有再開ダイアログを表示するか否かの値を更新
   */
  const changeIsShareActiveDialog = useCallback(
    (value: boolean) => {
      dispatch(setIsOpenShareActiveDialog(value));
    },
    [dispatch, setIsOpenShareActiveDialog],
  );

  /**
   * 共有一時停止ダイアログを表示するか否かの値を更新
   */
  const changeIsShareInactiveDialog = useCallback(
    (value: boolean) => {
      dispatch(setISOpenShareInactiveDialog(value));
    },
    [dispatch, setISOpenShareInactiveDialog],
  );

  /**
   * プラン情報取得APIレスポンスからプラン情報を更新
   */
  const changePlanInfoFromApi = useCallback(
    (value: ApiPlanInfo | undefined) => {
      dispatch(setPlanInfoFromApi(value));
    },
    [dispatch, setPlanInfoFromApi],
  );

  /**
   * 変換済みのプラン情報を更新
   */
  const changePlanInfo = useCallback(
    (value: PlanInfo) => {
      dispatch(setPlanInfo(value));
    },
    [dispatch, setPlanInfo],
  );

  /**
   * プラン情報を取得済かチェック
   */
  const isPlanInfoFetched = useMemo(
    () => JSON.stringify(planInfo) !== JSON.stringify(initialState.planInfo),
    [planInfo],
  );

  /**
   * 全てのStateをリセット
   */
  const resetState = useCallback(() => {
    dispatch(resetToInitialState());
  }, [dispatch, resetToInitialState]);

  /**
   * UI言語
   */
  const currentUiLanguage = useMemo(() => {
    if (uiLang) {
      return uiLang;
    }

    // ブラウザ言語を取得してデフォルト設定
    return defaultUiLanguage();
  }, [uiLang]);

  /**
   * Firestoreの共有画面状態を、共有状況の変更状態に変換
   */
  const newViewConfigStatusToActiveChangeStatus = useCallback(
    (newConfigViewStatus: ViewConfigStatus) => {
      if (!newConfigViewStatus) {
        return VIEW_CONFIG_STATUS.ACTIVE;
      }

      if (newConfigViewStatus === VIEW_CONFIG_STATUS.ACTIVE) {
        return VIEW_CONFIG_STATUS.ACTIVE;
      }

      return VIEW_CONFIG_STATUS.INACTIVE;
    },
    [],
  );

  /**
   * 新しい共有画面設定の状態を確認
   *
   * @returns true=共有中 / false=共有一時停止中
   */
  const checkNewViewConfigStatus = useCallback(
    (newConfigViewStatus: ViewConfigStatus) => {
      if (newConfigViewStatus === VIEW_CONFIG_STATUS.ACTIVE) {
        return true;
      }

      return false;
    },
    [],
  );

  /**
   * ホストが共有中か否か
   *
   * @returns true=共有中 / false=共有停止中
   */
  const isHostShareActive = useMemo(() => {
    if (shareActiveStatus === VIEW_CONFIG_STATUS.ACTIVE) {
      return true;
    }

    return false;
  }, [shareActiveStatus]);

  /**
   * キャンペーン関連を表示するか否か
   */
  const isCampaign: boolean = useMemo(
    () => planInfo.stt.view.promotion,
    [planInfo.stt.view.promotion],
  );

  /**
   * ホストの利用サービスがSentioか否か
   */
  const isHostSentio = useMemo(
    () => planInfo.service === SERVICE_MODE.SENTIO,
    [planInfo.service],
  );

  /**
   * ホストの利用サービスが旧プランか否か
   */
  const isHostSerial = useMemo(
    () => planInfo.service === SERVICE_MODE.LIVE,
    [planInfo.service],
  );

  return {
    displayType,
    fontSize,
    destlang,
    uiLang: currentUiLanguage,
    guestStatus,
    isCSVDownload,
    shareActiveStatus,
    isOpenShareActiveDialog,
    isOpenShareInactiveDialog,
    isCampaign,
    planInfo,
    isPlanInfoFetched,
    setDisplayType: changeDisplayType,
    setFontSize: changeFontSize,
    setDestlang: changeDestlang,
    setUiLang: changeUiLanguage,
    setGuestStatus: changeGuestStatus,
    setIsCSVDownload: changeIsCSVDownload,
    setShareActiveStatus: changeShareActiveStatus,
    setIsOpenShareActiveDialog: changeIsShareActiveDialog,
    setISOpenShareInactiveDialog: changeIsShareInactiveDialog,
    setPlanInfoFromApi: changePlanInfoFromApi,
    setPlanInfo: changePlanInfo,
    resetState,
    newViewConfigStatusToActiveChangeStatus,
    checkNewViewConfigStatus,
    isHostShareActive,
    isHostSentio,
    isHostSerial,
  };
};
